/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { classifySignableSigners } from './helper';
import { SignerSchema } from '../../definitions/schemas';
import TextWithIcon from '../TextWithIcon';
import useDarkTheme from '../../theme/styles/useDarkTheme';
import { useI18n } from '../../lib/hook/useI18n';

interface Props {
  children: React.ReactElement<any, any>;
  disablePortal?: boolean;
  signers: SignerSchema[];
}

const SignableStatusTooltip: React.FC<Props> = (props: Props): JSX.Element => {
  const darkThemeClasses = useDarkTheme();
  const { t } = useI18n();

  const { children, disablePortal, signers } = props;

  const [signed, pending] = useMemo(
    () => classifySignableSigners(signers),
    [signers]
  );

  const renderSigner = (
    signer: SignerSchema,
    includeEvent?: boolean
  ): JSX.Element => {
    const primary = signer.name || signer.email;
    const log = signer.lastEvent;

    const eventKey = log?.toLowerCase().replace(/ /g, '_');

    return (
      <>
        <Typography
          className={darkThemeClasses.primaryTextLight}
          variant="caption"
        >
          {primary}
        </Typography>

        {primary !== signer.email && signer.email && (
          <Typography
            className={darkThemeClasses.secondaryTextLight}
            sx={{ display: 'block' }}
            variant="caption"
          >
            {signer.email}
          </Typography>
        )}

        {includeEvent && log && (
          <Typography
            className={darkThemeClasses.secondaryTextLight}
            sx={{ display: 'block' }}
            variant="caption"
          >
            {t(`esignature.tooltip.${eventKey}`)}
          </Typography>
        )}
      </>
    );
  };

  const renderTooltipTitle = (): JSX.Element => (
    <Box p={0.5}>
      {signed.length > 0 && (
        <>
          <Box sx={{ color: 'var(--success-color-light)' }} mb={1}>
            <TextWithIcon
              bold
              color="inherit"
              icon={<CheckCircle fontSize="small" />}
              text={t('esignature.tooltip.signed')}
              variant="caption"
            />
          </Box>

          {signed.map((signer, index) => (
            <Box key={`signer-signed-${index}`}>{renderSigner(signer)}</Box>
          ))}
        </>
      )}
      {pending.length > 0 && (
        <>
          {signed.length > 0 && (
            <Box mx={-1.5} my={1}>
              <Divider className={darkThemeClasses.divider} />
            </Box>
          )}
          <Box sx={{ color: 'var(--warning-color-lighter)' }} mb={1}>
            <TextWithIcon
              bold
              color="inherit"
              icon={<AccessTime fontSize="small" />}
              text={t('esignature.tooltip.not_signed')}
              variant="caption"
            />
          </Box>
          {pending.map((signer, index) => (
            <Box key={`signer-pending-${index}`} mb={1}>
              {renderSigner(signer, true)}
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  return (
    <Tooltip
      placement="top"
      PopperProps={{ disablePortal }}
      title={renderTooltipTitle()}
    >
      {children}
    </Tooltip>
  );
};

export default SignableStatusTooltip;
